import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareWarning, Plus, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Workplace / Interpersonal", "Compensation & Benefits", "Facilities & Safety", "Policy / Process", "Harassment or Misconduct", "Other"];
const form0 = { category: "", subject: "", description: "", priority: "Normal" };

export default function MyGrievances() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(form0);

  const { data: items, isLoading } = useQuery({ queryKey: ["myGrievances"], queryFn: async () => (await api.get("/my/grievances")).data });

  const raise = useMutation({
    mutationFn: async () => (await api.post("/my/grievances", form)).data,
    onSuccess: () => { toast.success("Grievance submitted"); qc.invalidateQueries({ queryKey: ["myGrievances"] }); setOpen(false); setForm(form0); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const withdraw = useMutation({
    mutationFn: async (gid) => (await api.delete(`/my/grievances/${gid}`)).data,
    onSuccess: () => { toast.success("Grievance withdrawn"); qc.invalidateQueries({ queryKey: ["myGrievances"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader overline="Employee Voice" title="My Grievances" icon={MessageSquareWarning}
        subtitle="Raise a concern in confidence. It goes to your Reporting Officer for recommendation, then to HR for a final decision."
        actions={<Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setOpen(true)} data-testid="raise-grievance-btn"><Plus className="h-4 w-4 mr-1" /> Raise a Grievance</Button>} />

      {(items || []).length === 0 ? (
        <EmptyState icon={MessageSquareWarning} title="No grievances raised" hint="Anything you raise here is routed through your Reporting Officer to HR." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Category</TableHead>
              <TableHead className="overline text-slate-500">Subject</TableHead>
              <TableHead className="overline text-slate-500">Priority</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="overline text-slate-500">Remarks</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((g) => (
                <TableRow key={g.id} className="table-row-i" data-testid={`grievance-${g.id}`}>
                  <TableCell className="font-medium text-navy">{g.category}</TableCell>
                  <TableCell className="text-slate-600 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words">{g.subject}</TableCell>
                  <TableCell className="text-slate-600">{g.priority}</TableCell>
                  <TableCell><StatusBadge status={g.status} /></TableCell>
                  <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{g.remarks || g.ro_remarks || "—"}</TableCell>
                  <TableCell className="text-right">{["Pending_RO", "Pending_HR"].includes(g.status) && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => withdraw.mutate(g.id)} data-testid={`withdraw-${g.id}`}><X className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="raise-grievance-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Raise a Grievance</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="grievance-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subject *</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" data-testid="grievance-subject" /></div>
            <div><Label>Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={4} data-testid="grievance-description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.category || form.subject.length < 3 || form.description.length < 5 || raise.isPending} onClick={() => raise.mutate()} data-testid="submit-grievance">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
